import axios from 'axios'
import { store, Actions } from '../store'
import { toast } from 'react-toastify'
import { Helper } from '.'

export const APP_BASE_URL: string = process.env.APP_BASE_URL

// Create a hardened axios instance so all requests share sane defaults
const http = axios.create({
  baseURL: `${APP_BASE_URL}/`,
  timeout: 1000000000,
  // Only resolve promises for 2xx
  validateStatus: (status) => status >= 200 && status < 300,
  // XSRF support if server sets a cookie (harmless if unused)
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-CSRF-Token',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // Do not send credentials by default as we use Bearer auth; safe if later enabled
  withCredentials: false,
})
const storeProcess = async (config: any, data: any) => {
  const actionType: 'set' | 'append' | 'update' | 'remove' | 'reset' =
    config.action
  if (actionType !== 'reset') {
    store.dispatch(Actions[actionType](config.key, data))
  } else {
    store.dispatch(Actions['reset'](config.key))
  }
}

const loadingProcess = async (config: any, loading = false) => {
  if (!!config?.store) {
    const actionType: 'set' | 'update' | 'remove' | 'reset' =
      config?.store?.action
    if (actionType === 'set' || actionType === 'update') {
      const loadingData: any = {
        loading: loading,
        loadingState: true,
      }
      store.dispatch(Actions[actionType](config?.store?.key, loadingData))
    }
  }
}

const request = async (configuration: any) => {
  const { authorization, config, ...restConfiguration } = configuration
  const defaultHeader: any = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Api-Key': process.env.API_KEY,
  }

  if (!!authorization) {
    const user = Helper.getUser()
    if (!user?.token) {
      // toast.error('No token found')
      throw new Error('No token found')
    }
    defaultHeader.Authorization = `Bearer ${user?.token}`
  }

  await loadingProcess(config, true)
  return await http({
    ...restConfiguration,
    // rely on axios baseURL; ensure url is a string
    url: restConfiguration?.url?.toString?.() || restConfiguration?.url,
    headers: {
      ...defaultHeader,
      // Prevent accidental auth/header logging by not spreading configuration headers first
    },
  })
    .then(async (resp) => {
      if (!!resp?.data?.errors) {
        const firstErrorMessage =
          resp?.data?.errors?.[0]?.message || 'Request failed'
        throw new Error(firstErrorMessage)
      }
      const data = resp?.data?.data
      if (!!config.store) {
        await storeProcess(config.store, data)
      }
      if (!!configuration?.config?.successMsg) {
        toast.success(configuration?.config?.successMsg)
      }

      return data
    })
    .catch(async (err) => {
      const status = err?.response?.status
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.message ||
        err?.message ||
        'Something went wrong'
      if (!!config?.showErr) {
        toast.error(message)
      }

      if (status === 401 || message === 'Unauthorized') {
        Helper.removeUser()
        setTimeout(() => {
          window.location.reload()
        }, 700)
      } else {
        await loadingProcess(config, false)
      }

      // Re-throw a sanitized error to avoid leaking internals
      throw new Error(message)
    })
}

export default request
